import {
  PatientMedication,
  PatientMedicationDosageWhenEnum,
  PatientMedicationFrequencyEnum,
} from "@/services";
import { MedicationEntry, PrescriberOption } from "./readmission-f2f-types";
import { newId } from "./readmission-f2f-utils";

export const createMedicationEntry = (
  prescribedBy: PrescriberOption | null = null
): MedicationEntry => ({
  id: newId("medication"),
  medicineId: "",
  medicineName: "",
  quantity: "",
  dosage: "",
  frequency: "",
  dosageWhen: "",
  route: "",
  startDate: new Date().toISOString(),
  endDate: "",
  isLifetime: false,
  prescribedBy,
  note: "",
});

const toRecord = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null
    ? Object.fromEntries(Object.entries(value))
    : {};

const toText = (value: unknown): string =>
  typeof value === "string" ? value : "";

const toFirstPair = (value: unknown): PrescriberOption | null => {
  const [pair] = Object.entries(toRecord(value));
  return pair ? { value: pair[0], label: toText(pair[1]) } : null;
};

/** The medication list hook is typed { name, uuid } but returns the saved patient medications. */
export const toMedicationEntries = (content: unknown): MedicationEntry[] =>
  Array.isArray(content)
    ? content.map((item) => {
        const record = toRecord(item);
        const medicine = toFirstPair(record.medicalCodes);
        return {
          id: toText(record.uuid) || newId("medication"),
          uuid: toText(record.uuid) || undefined,
          medicineId: medicine?.value ?? "",
          medicineName: medicine?.label ?? "",
          quantity: toText(record.quantity),
          dosage: toText(record.dosage),
          frequency: toText(record.frequency),
          dosageWhen: toText(record.dosageWhen),
          route: toText(record.direction),
          startDate: toText(record.medicationStartDate),
          endDate: toText(record.medicationLastDate),
          isLifetime: record.lifeTime === true,
          prescribedBy: toFirstPair(record.prescribedBy),
          note: toText(record.note),
        };
      })
    : [];

export const isSameMedicationEntry = (
  a: MedicationEntry,
  b: MedicationEntry
): boolean =>
  JSON.stringify({ ...a, id: "" }) === JSON.stringify({ ...b, id: "" });

export const isMedicationEntryComplete = (entry: MedicationEntry): boolean =>
  !!entry.medicineId && !!entry.startDate;

export const buildMedicationPayload = (
  entry: MedicationEntry,
  patientId?: string,
  appointmentId?: string
): PatientMedication => ({
  uuid: entry.uuid,
  patient: { [patientId ?? ""]: "" },
  appointmentId: appointmentId ?? undefined,
  medicalCodes: { [entry.medicineId]: "" },
  quantity: entry.quantity || undefined,
  dosage: entry.dosage || undefined,
  frequency: Object.values(PatientMedicationFrequencyEnum).find(
    (it) => it === entry.frequency
  ),
  dosageWhen: Object.values(PatientMedicationDosageWhenEnum).find(
    (it) => it === entry.dosageWhen
  ),
  direction: entry.route || undefined,
  medicationStartDate: entry.startDate || undefined,
  medicationLastDate: entry.isLifetime ? undefined : entry.endDate || undefined,
  lifeTime: entry.isLifetime,
  prescribedBy: entry.prescribedBy
    ? { [entry.prescribedBy.value]: entry.prescribedBy.label }
    : undefined,
  note: entry.note.trim() || undefined,
});
